import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import AppLayout from './components/layout/AppLayout';
import AdminLayout from './components/layout/AdminLayout';
import ScrollToTop from './components/common/ScrollToTop';
import HomePage from './pages/HomePage';
import BrowsePage from './pages/BrowsePage';
import MoviesPage from './pages/MoviesPage';
import SeriesPage from './pages/SeriesPage';
import PopularPage from './pages/PopularPage';
import MoviePage from './pages/MoviePage';
import WatchPage from './pages/WatchPage';
import LoginPage from './pages/LoginPage';
import WhosWatchingPage from './pages/WhosWatchingPage';
import CreatorSignupPage from './pages/CreatorSignupPage';
import ActivateAccountPage from './pages/ActivateAccountPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import CategoriesPage from './pages/CategoriesPage';
import WatchlistPage from './pages/WatchlistPage';
import NotFoundPage from './pages/NotFoundPage';
import TermsPage from './pages/TermsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import SubscribePage from './pages/SubscribePage';
import ProfilePage from './pages/ProfilePage';
import TrailerPage from './pages/TrailerPage';
import ActorPage from './pages/ActorPage';
import LegalPage from './pages/LegalPage';
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import AdminMoviesPage from './pages/admin/AdminMoviesPage';
import AdminEpisodesPage from './pages/admin/AdminEpisodesPage';
import AdminConfigPage from './pages/admin/AdminConfigPage';
import AdminSubscriptionsPage from './pages/admin/AdminSubscriptionsPage';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminContentCreatorsPage from './pages/admin/AdminContentCreatorsPage';
import AdminCategoriesPage from './pages/admin/AdminCategoriesPage';
import AdminActorsPage from './pages/admin/AdminActorsPage';
import AdminSubscriptionPlansPage from './pages/admin/AdminSubscriptionPlansPage';
import AdminCatalogPage from './pages/admin/AdminCatalogPage';
import AdminPayoutRunsPage from './pages/admin/AdminPayoutRunsPage';
import AdminPayoutConfigPage from './pages/admin/AdminPayoutConfigPage';
import AdminLegalPagesPage from './pages/admin/AdminLegalPagesPage';
import UploadMoviePage from './pages/UploadMoviePage';
import UploadEpisodePage from './pages/UploadEpisodePage';
import CreatorDashboardPage from './pages/CreatorDashboardPage';
import ProducerPayoutPage from './pages/ProducerPayoutPage';
import { useAuthStore } from './store';
import { AlertProvider } from './components/ui/AlertModal';
import { MyListProvider } from './lib/my-list-context';
import { payoutApi } from './api/client';
import { getPendingHeartbeat, clearPendingHeartbeat } from './utils/pendingHeartbeat';
import './styles/globals.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        const status = error?.response?.status;
        if (status && status < 500) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
    },
  },
});

// ── Route guards ──────────────────────────────────────────────────────────────
function RequireAuth({ children }) {
  const { token } = useAuthStore();
  if (!token) return <Navigate to="/login" replace />;
  return children;
}

function RequireAdmin({ children }) {
  const { token, user } = useAuthStore();
  if (!token) return <Navigate to="/login" replace />;
  if (user?.Role !== 'Admin') return <Navigate to="/" replace />;
  return children;
}

function RequireCreator({ children }) {
  const { token, user } = useAuthStore();
  if (!token) return <Navigate to="/login" replace />;
  if (user?.Role !== 'ContentCreator' && user?.Role !== 'Admin') return <Navigate to="/" replace />;
  return children;
}

// Gate for the actual upload pages: being a ContentCreator isn't enough on its own — an admin
// must have separately granted upload access (AppUser.CanUploadContent / user.CanUploadContent
// from the JWT). Admins bypass this, same as RequireCreator above.
function RequireUploadAccess({ children }) {
  const { token, user } = useAuthStore();
  if (!token) return <Navigate to="/login" replace />;
  if (user?.Role === 'Admin') return children;
  if (user?.Role !== 'ContentCreator' || !user?.CanUploadContent) return <Navigate to="/creator" replace />;
  return children;
}

// Netflix-style profile gate: a signed-in account with profiles on it (every account has at
// least one, per AuthResponseDto.Profiles) must pick one via WhosWatchingPage before reaching
// anything else — that choice is what puts profileId/isKids into the JWT so Kids-profile
// filtering (MovieApiController/HomeApiController) actually applies. An anonymous visitor
// (no token) skips this entirely and just browses normally.
function RequireProfile({ children }) {
  const { token, activeProfile, profiles } = useAuthStore();
  if (token && !activeProfile && profiles && profiles.length > 0) {
    return <Navigate to="/whos-watching" replace />;
  }
  return children;
}

export default function App() {
  const { token } = useAuthStore();

  // The browser's own scroll restoration fights our explicit scroll-to-top on navigation
  // (it tries to restore the previous position on reload / back-forward) — hand that off to
  // ScrollToTop instead, which resets on every route change.
  React.useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  // A heartbeat that failed mid-video because the token had expired is queued (see
  // utils/pendingHeartbeat.js) instead of dropped. As soon as a valid token is available again
  // — sign-in, or already-signed-in on page load — flush it once so that watch time isn't
  // silently lost to a session expiry.
  React.useEffect(() => {
    if (!token) return;
    const pending = getPendingHeartbeat();
    if (!pending) return;

    payoutApi
      .heartbeat(pending.episodeId, pending.clientSessionId, pending.secondsWatchedTotal, pending.deviceFingerprint)
      .catch(() => {}) // best-effort recovery — if it fails again there's nothing more useful to do than drop it
      .finally(() => clearPendingHeartbeat());
  }, [token]);

  return (
    <QueryClientProvider client={queryClient}>
      <AlertProvider>
        <MyListProvider>
          <BrowserRouter>
            <ScrollToTop />
            <Routes>
            {/* Full-screen routes (no layout) */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/whos-watching" element={<RequireAuth><WhosWatchingPage /></RequireAuth>} />
            <Route path="/creator-signup" element={<CreatorSignupPage />} />
            <Route path="/activate" element={<ActivateAccountPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/watch/:episodeId" element={
              <RequireAuth><RequireProfile><WatchPage /></RequireProfile></RequireAuth>
            } />
            <Route path="/trailer/:movieId" element={<TrailerPage />} />

            {/* Public legal pages use the site chrome but remain accessible without a viewer profile. */}
            <Route element={<AppLayout />}>
              <Route path="/terms" element={<LegalPage slug="terms" />} />
              <Route path="/privacy" element={<LegalPage slug="privacy" />} />
            </Route>

            {/* Main layout routes — gated on a viewer profile being selected whenever the visitor is signed in */}
            <Route element={<RequireProfile><AppLayout /></RequireProfile>}>
              <Route path="/" element={<HomePage />} />
              <Route path="/browse" element={<BrowsePage />} />
              <Route path="/movies" element={<MoviesPage />} />
              <Route path="/series" element={<SeriesPage />} />
              <Route path="/popular" element={<PopularPage />} />
              <Route path="/categories" element={<CategoriesPage />} />
              <Route path="/movie/:id" element={<MoviePage />} />
              <Route path="/actor/:actorId" element={<ActorPage />} />
              <Route path="/watchlist" element={<RequireAuth><WatchlistPage /></RequireAuth>} />
              <Route path="/profile" element={<RequireAuth><ProfilePage /></RequireAuth>} />
              <Route path="/subscribe" element={<SubscribePage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />

              <Route path="*" element={<NotFoundPage />} />
            </Route>

            {/* Admin console — its own sidebar shell instead of the main site chrome */}
            <Route
              path="/admin"
              element={
                <RequireProfile>
                  <RequireAdmin>
                    <AdminLayout />
                  </RequireAdmin>
                </RequireProfile>
              }
            >
              <Route index element={<AdminDashboardPage />} />
              <Route path="movies" element={<AdminMoviesPage />} />
              <Route path="episodes" element={<AdminEpisodesPage />} />
              <Route path="config" element={<AdminConfigPage />} />
              <Route path="legal-pages" element={<AdminLegalPagesPage />} />
              <Route path="subscriptions" element={<AdminSubscriptionsPage />} />
              <Route path="users" element={<AdminUsersPage />} />
              <Route path="content-creators" element={<AdminContentCreatorsPage />} />
              <Route path="categories" element={<AdminCategoriesPage />} />
              <Route path="actors" element={<AdminActorsPage />} />
              <Route path="plans" element={<AdminSubscriptionPlansPage />} />
              <Route path="catalog" element={<AdminCatalogPage />} />
              <Route path="payouts" element={<AdminPayoutRunsPage />} />
              <Route path="payouts/config" element={<AdminPayoutConfigPage />} />
            </Route>

            {/* Content Creator dashboard — reuses the admin console's sidebar shell */}
            <Route
              path="/creator"
              element={
                <RequireProfile>
                  <RequireCreator>
                    <AdminLayout />
                  </RequireCreator>
                </RequireProfile>
              }
            >
              <Route index element={<CreatorDashboardPage />} />
              <Route path="payouts" element={<ProducerPayoutPage />} />
            </Route>

            {/* Upload routes — reuse the admin console shell */}
            <Route
              path="/upload"
              element={
                <RequireProfile>
                  <RequireUploadAccess>
                    <AdminLayout />
                  </RequireUploadAccess>
                </RequireProfile>
              }
            >
              <Route path="movie" element={<UploadMoviePage />} />
              <Route path="episode" element={<UploadEpisodePage />} />
            </Route>
          </Routes>
          </BrowserRouter>
        </MyListProvider>
      </AlertProvider>
    </QueryClientProvider>
  );
}
